/**
 * Endless Game Mode
 * 
 * The primary game mode featuring Puyo Puyo-style matching mechanics
 * with infinite progression based on configurable difficulty settings.
 * 
 * Game continues indefinitely with increasing difficulty until Game Over.
 * All cell types (including cancer) are treated equally for matching.
 * 
 * Difficulty progression systems:
 * - Easy: Static level 3 throughout the game (like Time Attack mode)
 * - Normal: 120 seconds per level (30 minutes to reach level 15)
 * - Hard: 45 seconds per level (11.25 minutes to reach level 15)
 */

import { BaseGameMode, GameState, ModeConfig } from '../core/BaseGameMode'
import { Grid } from '../Grid'
import { BaseMatchingEngine, MatchResult } from '../core/BaseMatchingEngine'
import { FallingPair, PairGenerator, PairConfiguration } from '../FallingPair'
import { GameBalanceManager } from '../GameBalanceManager'
import { GameStorage } from '../../utils/GameStorage'
import { SettingsManager } from '../SettingsManager'

export class EndlessGameMode extends BaseGameMode {
    // Endless mode specific state
    private nextPairs: PairConfiguration[]
    private callbacks: Map<string, Function[]>
    private currentLevel: number = 1 // Track current level for automatic level up detection

    constructor(
        grid: Grid,
        matchingEngine: BaseMatchingEngine,
        pairGenerator: PairGenerator,
        balanceManager: GameBalanceManager,
        config: ModeConfig
    ) {
        super(grid, matchingEngine, pairGenerator, balanceManager, config)

        this.nextPairs = []
        this.callbacks = new Map()
        this.currentLevel = 1 // Initialize starting level
    }

    /**
     * Initialize Endless mode - generate first set of pairs
     */
    public initialize(): void {
        this.generateNextPairs()
        this.spawnNextPair()
        this.emit('gameStarted', this.stats)
    }

    /**
     * Update game state based on delta time
     */
    public update(deltaTime: number): void {
        if (
            this.gameState === GameState.PAUSED ||
            this.gameState === GameState.WAITING_TO_START ||
            this.gameState === GameState.GAME_OVER ||
            this.gameState === GameState.VICTORY
        ) {
            return
        }

        this.updateTimeStats()

        // Check for automatic level ups (time-based, not match-dependent)
        this.checkForLevelUp()

        switch (this.gameState) {
            case GameState.PLAYING:
                this.updatePlaying(deltaTime)
                break
            case GameState.FALLING:
                this.updateFalling(deltaTime)
                break
            case GameState.MATCHING:
                this.updateMatching()
                break
        }
    }

    /**
     * Handle player input
     */
    public handleInput(action: string): boolean {
        // Handle pause/unpause regardless of game state
        if (action === 'pause') {
            this.pauseGame()
            return true
        }

        if (!this.currentPair || this.gameState !== GameState.PLAYING) {
            return false
        } switch (action) {
            case 'moveLeft':
                return this.currentPair.moveHorizontal(-1)
            case 'moveRight':
                return this.currentPair.moveHorizontal(1)
            case 'rotateClockwise':
                return this.currentPair.rotate(1)
            case 'rotateCounterClockwise':
                return this.currentPair.rotate(-1)
            case 'softDrop':
                return this.currentPair.moveDown()
            case 'hardDrop':
                this.currentPair.hardDrop()
                return true
            default:
                return false
        }
    }

    /**
     * Pause the game and emit event
     */
    public pauseGame(): void {
        super.pauseGame()
        if (this.gameState === GameState.PAUSED) {
            this.emit('gamePaused')
        }
    }

    /**
     * Resume the game and emit event
     */
    public resumeGame(): void {
        super.resumeGame()
        if (this.gameState === GameState.PLAYING) {
            this.emit('gameResumed')
        }
    }

    /**
     * Get preview of next pairs
     */
    public getNextPairs(): PairConfiguration[] {
        return [...this.nextPairs]
    }

    /**
     * Register event callbacks
     */
    public on(event: string, callback: Function): void {
        if (!this.callbacks.has(event)) {
            this.callbacks.set(event, [])
        }
        this.callbacks.get(event)!.push(callback)
    }

    /**
     * Remove event callbacks
     */
    public off(event: string, callback?: Function): void {
        if (!this.callbacks.has(event)) return

        if (callback) {
            const callbacks = this.callbacks.get(event)!
            const index = callbacks.indexOf(callback)
            if (index > -1) {
                callbacks.splice(index, 1)
            }
        } else {
            this.callbacks.set(event, [])
        }
    }

    /**
     * Backward compatible helper for tests expecting getActivePiece()
     */
    public getActivePiece(): { rotation: number; exists: boolean; x: number; y: number } | null {
        if (!this.currentPair) return null
        const state = this.currentPair.getState()
        return { rotation: state.rotation, exists: true, x: state.x, y: state.y }
    }

    /**
     * Get the current falling pair (for rendering)
     */
    public getCurrentPair(): FallingPair | null {
        return this.currentPair
    }

    // ========================================
    // Protected Methods (Mode-Specific Logic)
    // ========================================

    /**
     * Check if game should end - Endless mode only ends on Game Over
     */
    protected checkEndConditions(): void {
        if (this.grid.isOverflowing()) {
            this.gameState = GameState.GAME_OVER
            this.emit('gameOver', this.stats)
        }
        // No victory condition - Endless mode continues indefinitely until Game Over
    }

    /**
     * Calculate score for cleared cells with chain multiplier
     */
    protected calculateScore(cellsCleared: number, chainLevel: number): number {
        // Score is calculated by matching engine, but can be overridden here
        const baseScore = cellsCleared * 10
        const chainMultiplier = chainLevel || 1
        const totalScore = baseScore * chainMultiplier

        console.log(`🎯 SCORE DEBUG: Cells cleared: ${cellsCleared}, Chain level: ${chainLevel}, Base score: ${baseScore}, Chain multiplier: ${chainMultiplier}x, Total score: ${totalScore}`)

        return totalScore
    }

    /**
     * Check for automatic level ups based on time progression
     * Emits engraftmentLevelUp event when level increases
     * Note: Easy difficulty stays at level 3, so no level-up events are emitted
     */
    private checkForLevelUp(): void {
        const newLevel = this.getTimeBasedLevel()

        if (newLevel > this.currentLevel) {
            console.log(`🎉 AUTOMATIC LEVEL UP! Previous level: ${this.currentLevel}, New level: ${newLevel}`)

            this.currentLevel = newLevel

            // Update unlock progress in GameStorage
            GameStorage.updateLevelProgress('ENDLESS', newLevel)

            // Emit level up event to trigger visual/audio effects
            // Skip for Easy difficulty since it stays at level 3
            const difficulty = SettingsManager.getInstance().getDifficulty()
            if (difficulty !== 'Easy') {
                this.emit('engraftmentLevelUp', newLevel)
            }
        }
    }

    /**
     * Calculate current level based on time elapsed for endless progression
     * Uses difficulty-based progression systems:
     * - Easy: Static level 3 (like Time Attack mode)
     * - Normal: 120 seconds per level (30 minutes to reach level 15)
     * - Hard: 45 seconds per level (11.25 minutes to reach level 15)
     */
    public getTimeBasedLevel(): number {
        const difficulty = SettingsManager.getInstance().getDifficulty()
        const timeInSeconds = this.stats.timeElapsed

        let currentLevel: number
        let nextLevelTime: number
        let timeUntilNext: number

        switch (difficulty) {
            case 'Easy':
                // Static level 3 throughout the game (like Time Attack mode)
                currentLevel = 3
                nextLevelTime = 0
                timeUntilNext = 0
                console.log(`⏰ LEVEL DEBUG (Easy): Static level 3`)
                break

            case 'Normal':
                // 120 seconds per level (30 minutes total to level 15)
                currentLevel = Math.floor(timeInSeconds / 120) + 1
                currentLevel = Math.min(currentLevel, 15)
                nextLevelTime = Math.min(currentLevel * 120, 15 * 120)
                timeUntilNext = currentLevel < 15 ? nextLevelTime - timeInSeconds : 0
                console.log(`⏰ LEVEL DEBUG (Normal): Time elapsed: ${timeInSeconds.toFixed(1)}s, Level: ${currentLevel}, Time until next level: ${timeUntilNext.toFixed(1)}s`)
                break

            case 'Hard':
            default:
                // 45 seconds per level (11.25 minutes total to level 15) - original behavior
                currentLevel = Math.floor(timeInSeconds / 45) + 1
                currentLevel = Math.min(currentLevel, 15)
                nextLevelTime = Math.min(currentLevel * 45, 15 * 45)
                timeUntilNext = currentLevel < 15 ? nextLevelTime - timeInSeconds : 0
                console.log(`⏰ LEVEL DEBUG (Hard): Time elapsed: ${timeInSeconds.toFixed(1)}s, Level: ${currentLevel}, Time until next level: ${timeUntilNext.toFixed(1)}s`)
                break
        }

        return currentLevel
    }    // ========================================
    // Private Methods (Update Loop)
    // ========================================

    /**
     * Update the playing state
     */
    private updatePlaying(deltaTime: number): void {
        if (!this.currentPair) {
            this.spawnNextPair()
            return
        }

        const stillFalling = this.currentPair.update(deltaTime)

        if (!stillFalling) {
            // Pair has locked
            this.stats.pairsDropped++
            this.currentPair = null
            this.gameState = GameState.MATCHING
            this.emit('pairLocked')
        }
    }

    /**
     * Update the falling state (cells falling due to gravity)
     */
    private updateFalling(deltaTime: number): void {
        // Optional chain pacing pause
        if ((this as any)._chainPauseMs && (this as any)._chainPauseMs > 0) {
            (this as any)._chainPauseMs -= deltaTime
            if ((this as any)._chainPauseMs > 0) {
                return // Hold before allowing gravity to settle
            }
        }
        const cellsMoved = this.grid.applyGravity()

        if (!cellsMoved) {
            // All cells have settled, check for new matches
            this.gameState = GameState.MATCHING
        }
    }

    /**
     * Update the matching state
     */
    private updateMatching(): void {
        const matchResult = this.matchingEngine.processMatches()

        if (matchResult.totalCellsCleared > 0) {
            // Process match results
            this.processMatchResult(matchResult)

            // Check if more cells need to fall
            this.gameState = GameState.FALLING
        } else {
            // No matches, check game state
            this.checkEndConditions()

            if (this.gameState !== GameState.GAME_OVER && this.gameState !== GameState.VICTORY) {
                this.gameState = GameState.PLAYING
                this.spawnNextPair()
            }
        }
    }

    /**
     * Update purify mode
     */
    private updatePurifyMode(): void {
        // Purify mode is legacy - kept for compatibility
        // In endless mode, no special purify logic
        this.gameState = GameState.FALLING
        this.emit('purifyCompleted', { totalCellsCleared: 0 })
    }

    /**
     * Spawn the next falling pair
     */
    private spawnNextPair(): void {
        // Ensure we always have at least 2 pairs in the queue for preview
        if (this.nextPairs.length < 2) {
            this.generateNextPairs()
        }

        const nextConfig = this.nextPairs.shift()!
        this.currentPair = new FallingPair(nextConfig, this.grid)

        // Adjust fall speed based on time level for endless progression
        const timeLevel = this.getTimeBasedLevel()
        // Cap difficulty scaling at level 15 to prevent impossibly fast speeds
        const difficultyLevel = Math.min(timeLevel, 15)
        const fallSpeed = this.balanceManager.calculateFallSpeed(difficultyLevel)
        this.currentPair.setFallSpeed(fallSpeed)

        // Check if the new pair can be placed
        if (this.currentPair.isLocked()) {
            // Grid is full, game over
            this.gameState = GameState.GAME_OVER
            this.emit('gameOver', this.stats)
        } else {
            this.emit('pairSpawned', nextConfig)
        }
    }

    /**
     * Generate the next set of pairs with balanced cell distribution
     */
    private generateNextPairs(): void {
        const timeLevel = this.getTimeBasedLevel()

        // In endless mode, all cell types are treated equally for balanced gameplay
        // No special cancer scaling - cancer cells match like any other color
        this.pairGenerator.adjustWeights(0, Math.min(timeLevel, 10))

        // Generate preview pairs
        this.nextPairs = this.pairGenerator.previewNext(5)
    }

    /**
     * Process the result of matches
     */
    private processMatchResult(result: MatchResult): void {
        const previousScore = this.stats.score

        this.stats.score += result.scoreGained
        this.stats.totalCellsCleared += result.totalCellsCleared
        this.stats.longestChain = Math.max(this.stats.longestChain, result.chainLevel)

        console.log(`📊 MATCH RESULT DEBUG: Score gained: ${result.scoreGained}, Previous score: ${previousScore}, New score: ${this.stats.score}`)
        console.log(`🔄 STATS UPDATE: Cells cleared: ${result.totalCellsCleared}, Total cells: ${this.stats.totalCellsCleared}, Chain: ${result.chainLevel}, Longest chain: ${this.stats.longestChain}`)

        // Update balance manager with match data
        this.balanceManager.recordMatchResult({
            score: result.scoreGained,
            chainLevel: result.chainLevel,
            cellsCleared: result.totalCellsCleared,
            engraftmentLevel: this.getTimeBasedLevel() // Use time-based level for endless mode
        })

        // No engraftment system - just emit match processed
        this.emit('matchProcessed', result)

        // Chain pacing: add a pause proportional to chain depth (excluding first level)
        if (result.chainLevel > 1) {
            const BASE_CHAIN_PAUSE = 350 // ms
            const EXTRA_PER_LEVEL = 120 // ms
            const pause = BASE_CHAIN_PAUSE + (result.chainLevel - 2) * EXTRA_PER_LEVEL
                ; (this as any)._chainPauseMs = pause
        } else {
            ; (this as any)._chainPauseMs = 0
        }
    }

    /**
     * Emit events to registered callbacks
     */
    protected emit(event: string, data?: any): void {
        const callbacks = this.callbacks.get(event)
        if (callbacks) {
            callbacks.forEach(callback => callback(data))
        }
    }
}
